import styles from './styles.css.js';
import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { fireNonCancelableEvent } from '../../events';
import { getDropdownPosition, getInteriorDropdownPosition, } from './dropdown-fit-handler';
import { Transition } from '../transition';
import { useVisualRefresh } from '../../hooks/use-visual-mode';
import mergeRefs from 'react-merge-refs';
var DropdownContainer = function (_a) {
    var children = _a.children, _b = _a.renderWithPortal, renderWithPortal = _b === void 0 ? false : _b, id = _a.id, open = _a.open;
    if (renderWithPortal) {
        if (open) {
            return createPortal(React.createElement("div", { id: id }, children), document.body);
        }
        else {
            return null;
        }
    }
    else {
        return React.createElement(React.Fragment, null, children);
    }
};
var Dropdown = function (_a) {
    var children = _a.children, trigger = _a.trigger, open = _a.open, onDropdownClose = _a.onDropdownClose, onMouseDown = _a.onMouseDown, header = _a.header, footer = _a.footer, dropdownId = _a.dropdownId, _b = _a.stretchTriggerHeight, stretchTriggerHeight = _b === void 0 ? false : _b, _c = _a.stretchWidth, stretchWidth = _c === void 0 ? true : _c, _d = _a.stretchHeight, stretchHeight = _d === void 0 ? false : _d, _e = _a.stretchToTriggerWidth, stretchToTriggerWidth = _e === void 0 ? true : _e, _f = _a.expandToViewport, expandToViewport = _f === void 0 ? false : _f, _g = _a.preferCenter, preferCenter = _g === void 0 ? false : _g, _h = _a.interior, interior = _h === void 0 ? false : _h, minWidth = _a.minWidth, _j = _a.hasContent, hasContent = _j === void 0 ? true : _j;
    var triggerRef = useRef(null);
    var dropdownRef = useRef(null);
    // This container is only needed to apply max-height to. We can't move max-height to it's parent
    // because of an IE11 issue with flexbox. https://github.com/philipwalton/flexbugs/issues/216
    var verticalContainerRef = useRef(null);
    // To keep track of the initial position (drop up/down) which is kept the same during fixed repositioning
    var fixedPosition = useRef(null);
    var isRefresh = useVisualRefresh(triggerRef);
    var setDropdownPosition = function (position, target, verticalContainer) {
        var entireWidth = !interior && stretchWidth;
        if (!stretchHeight) {
            if (!stretchWidth) {
                // 1px offset for for dropdowns where the dropdown itself needs a border, rather than on the items
                verticalContainer.style.maxHeight = parseInt(position.height) + 1 + "px";
            }
            else {
                verticalContainer.style.maxHeight = position.height;
            }
        }
        if (entireWidth && !expandToViewport) {
            if (stretchToTriggerWidth) {
                target.classList.add(styles['occupy-entire-width']);
            }
        }
        else {
            target.style.width = position.width;
        }
        // Using styles for main dropdown to adjust its position as preferred alternative
        if (position.dropUp && !interior) {
            target.classList.add(styles['dropdown-drop-up']);
            if (!expandToViewport) {
                target.style.bottom = '100%';
            }
        }
        else {
            target.classList.remove(styles['dropdown-drop-up']);
        }
        target.classList.add(position.dropLeft ? styles['dropdown-drop-left'] : styles['dropdown-drop-right']);
        if (position.left && position.left !== 'auto') {
            target.style.left = position.left;
        }
        // For dropdowns with overflow enabled (expandToFit prop) we need to set the exact position based on the trigger
        var setPosition = function (position) {
            return position.triggerBottom !== undefined;
        };
        // Position normal overflow dropdowns with fixed positioning relative to viewport
        if (expandToViewport && !interior && setPosition(position)) {
            target.style.position = 'fixed';
            if (position.dropUp) {
                target.style.bottom = "calc(100% - " + position.triggerTop + ")";
            }
            else {
                target.style.top = position.triggerBottom;
            }
            if (position.dropLeft) {
                target.style.left = position.triggerRight;
                target.style.transform = "translateX(-" + position.width + ")";
            }
            else {
                target.style.left = position.triggerLeft;
            }
            // Keep track of the initial dropdown position and direction.
            // Dropdown direction doesn't need to change as the user scrolls, just needs to stay attached to the trigger.
            fixedPosition.current = position;
            return;
        }
        // For an interior dropdown (the fly out) we need exact values for positioning
        // and classes are not enough
        // usage of relative position is impossible due to overwrite of overflow-x
        var isInteriorPosition = function (position) { return position.bottom !== undefined; };
        if (interior && isInteriorPosition(position)) {
            if (position.dropUp) {
                target.style.bottom = position.bottom;
            }
            else {
                target.style.top = position.top;
            }
            target.style.left = position.left;
            return;
        }
    };
    // Update the dropdown location when fixed position is applied (expandToFit)
    var updateDropdownPosition = function () {
        if (triggerRef.current && dropdownRef.current && verticalContainerRef.current) {
            var triggerRect = triggerRef.current.getBoundingClientRect();
            var target = dropdownRef.current;
            if (fixedPosition.current) {
                if (fixedPosition.current.dropUp) {
                    dropdownRef.current.style.bottom = "calc(100% - " + triggerRect.top + "px)";
                }
                else {
                    target.style.top = triggerRect.bottom + "px";
                }
                if (fixedPosition.current.dropLeft) {
                    target.style.left = triggerRect.right + "px";
                    target.style.transform = "translateX(-" + fixedPosition.current.width + ")";
                }
                else {
                    target.style.left = triggerRect.left + "px";
                }
            }
        }
    };
    useLayoutEffect(function () {
        if (open && dropdownRef.current && triggerRef.current && verticalContainerRef.current) {
            // cleaning previously assigned values,
            // so that they are not reused in case of screen resize and similar events
            verticalContainerRef.current.style.maxHeight = '';
            dropdownRef.current.style.width = '';
            dropdownRef.current.style.top = '';
            dropdownRef.current.style.bottom = '';
            dropdownRef.current.style.left = '';
            dropdownRef.current.classList.remove(styles['dropdown-drop-left']);
            dropdownRef.current.classList.remove(styles['dropdown-drop-right']);
            dropdownRef.current.classList.remove(styles['dropdown-drop-up']);
            dropdownRef.current.classList.add(styles.nowrap);
            var position = interior
                ? getInteriorDropdownPosition(triggerRef.current, dropdownRef.current, expandToViewport)
                : getDropdownPosition(triggerRef.current, dropdownRef.current, expandToViewport, minWidth, preferCenter);
            setDropdownPosition(position, dropdownRef.current, verticalContainerRef.current);
            dropdownRef.current.classList.remove(styles.nowrap);
        }
        // https://issues.amazon.com/issues/AWSUI-13040
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dropdownRef, triggerRef, verticalContainerRef, interior, stretchWidth]);
    // subscribe to outside click
    useEffect(function () {
        if (!open) {
            return;
        }
        var clickListener = function (e) {
            var _a;
            if (!((_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                fireNonCancelableEvent(onDropdownClose);
            }
        };
        /*
         * This small delay allows the event that opened the dropdown to
         * finish bubbling, so that it is not immediately captured here.
         */
        var timeout = setTimeout(function () {
            window.addEventListener('click', clickListener);
        }, 0);
        return function () {
            clearTimeout(timeout);
            window.removeEventListener('click', clickListener);
        };
    }, [open, onDropdownClose]);
    // subscribe to window resize for fixed repositioning
    useEffect(function () {
        var resizeListener = function () { return expandToViewport && open && updateDropdownPosition(); };
        window.addEventListener('resize', resizeListener, true);
        return function () {
            window.removeEventListener('resize', resizeListener, true);
        };
        // https://issues.amazon.com/issues/AWSUI-13040
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, onDropdownClose, fixedPosition]);
    // subscribe to scroll for fixed repositioning
    useEffect(function () {
        var scrollListener = function () {
            expandToViewport && open && updateDropdownPosition();
        };
        window.addEventListener('scroll', scrollListener, true);
        return function () {
            window.removeEventListener('scroll', scrollListener, true);
        };
        // https://issues.amazon.com/issues/AWSUI-13040
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, onDropdownClose, fixedPosition]);
    return (React.createElement("div", { className: clsx(styles.root, interior && styles.interior, stretchTriggerHeight && styles['stretch-trigger-height']) },
        React.createElement("div", { className: clsx(stretchTriggerHeight && styles['stretch-trigger-height']), ref: triggerRef }, trigger),
        React.createElement(DropdownContainer, { renderWithPortal: expandToViewport && !interior, id: dropdownId, open: open },
            React.createElement(Transition, { "in": open || false, exit: false }, function (state, ref) {
                var _a;
                return (React.createElement("div", { className: clsx(styles.dropdown, (_a = {},
                        _a[styles.open] = open,
                        _a[styles['with-limited-width']] = !stretchWidth,
                        _a[styles['hide-upper-border']] = stretchWidth,
                        _a[styles.interior] = interior,
                        _a[styles['is-empty']] = !header && !hasContent,
                        _a[styles.refresh] = isRefresh,
                        _a)), ref: mergeRefs([dropdownRef, ref]), "data-open": open, "data-animating": state !== 'exited', onMouseDown: onMouseDown },
                    React.createElement("div", { className: clsx(styles['dropdown-content-wrapper'], isRefresh && styles.refresh) },
                        React.createElement("div", { className: styles['ie11-wrapper'] },
                            React.createElement("div", { ref: verticalContainerRef, className: styles['dropdown-content'] },
                                header,
                                children,
                                footer)))));
            }))));
};
export default Dropdown;
//# sourceMappingURL=index.js.map